import { EventBus, EVENTS } from '../../../shared/EventBus';
import { PixiOverlayHost } from '../../rendering/PixiOverlayHost';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { getMotionPrefs } from '../accessibility/motionPrefs';
import { TIME_OF_DAY, baseSceneMoodOf, isClockExemptScene, type TimeOfDayState } from '../../../config/atmosphere/timeOfDay.config';
import { TimeOfDayModel } from './TimeOfDayModel';

/**
 * TimeOfDayDriver — the TEMPORAL atmosphere bridge (sibling of CombatMoodDriver).
 * Advances a single slow global day/night clock and feeds the EXISTING scene
 * seams so the world breathes over real time:
 *
 *   tick (PixiOverlayHost) → TimeOfDayModel.advance → EVENTS.SCENE_MOOD
 *     → AtmosphereLayer multiplies the diurnal mood onto every mooded layer.
 *   ── phase 2 ──  → EVENTS.TIME_OF_DAY { phase, phase01, grade }
 *     → AtmosphereLayer blends the authored colorGrade toward the time grade
 *       (night = cool-dark) AND applies per-layer day-response multipliers
 *       (godRays dim at night, fog thickens) from timeOfDayResponse.config.
 *
 * The emitted mood is `base × diurnal`, where `base` is the level's authored
 * `scene.sceneMood` (default 1). So a level's resting intensity is preserved and
 * `final = base × diurnal × combat` composes cleanly (the day clock shapes the
 * level's mood, it doesn't overwrite it).
 *
 * Per-scene opt-out: interiors + authored always-night maps (the Coil
 * Undercroft) ignore the sky clock (`isClockExemptScene`) — the driver holds off
 * BOTH SCENE_MOOD and TIME_OF_DAY there so the preset's own eternal light rules
 * (the undercroft never "brightens at noon" nor gets a night grade). Reduce-motion
 * safe: the clock still advances (so it's at the right phase if the setting
 * flips) but the driver emits nothing — the atmosphere modulation ticker is off
 * under reduce-motion anyway, and the slow cycle never strobes.
 *
 * Mount race: AtmosphereLayer re-seeds the per-level base mood inside its
 * DEFERRED mount (after texture/host readiness), which would clobber a mood the
 * driver emitted first. A short post-load "settle window" re-asserts the diurnal
 * mood + time grade every frame until the mount has surely run, then falls back
 * to delta-throttled emits.
 *
 * Client-presentation only — zero server/sim impact, MP-safe.
 * See docs/design/TIME_OF_DAY.md.
 */
const SETTLE_MS = 2500; // covers the deferred mount's setSceneMood(base) reset

class TimeOfDayDriver {
  private clock = new TimeOfDayModel();
  private unsubTick: (() => void) | null = null;
  private unsubLevel: (() => void) | null = null;
  private exempt = false;
  /** The current level's authored base scene-mood; the diurnal factor multiplies onto it. */
  private baseMood = 1;
  private settleMsLeft = 0;
  private lastMoodEmitted = -1;
  private lastGradeEmitted: { top: number; bottom: number; strength: number } | null = null;
  private started = false;

  /** Idempotent. No-op when disabled in config. */
  start(): void {
    if (this.started || !TIME_OF_DAY.enabled) return;
    this.started = true;
    // Debug/QA surface — the console + 🌗 tuner can jump the clock + read state.
    try { (globalThis as { __timeOfDay?: unknown }).__timeOfDay = this; } catch { /* no global */ }
    const bus = EventBus.getInstance();
    this.unsubLevel = bus.on(EVENTS.LEVEL_LOADED, (d: { id?: string }) => {
      this.exempt = d?.id ? isClockExemptScene(d.id) : false;
      this.baseMood = d?.id ? this.readBaseMood(d.id) : 1;
      this.lastMoodEmitted = -1;        // force a fresh emit on the new scene
      this.lastGradeEmitted = null;     // force a fresh grade emit too
      this.settleMsLeft = SETTLE_MS;    // re-assert past the deferred mount reset
    });
    this.unsubTick = PixiOverlayHost.addTickListener((dtMs) => this.tick(dtMs));
  }

  private tick(dtMs: number): void {
    this.clock.tick(dtMs);
    if (this.settleMsLeft > 0) this.settleMsLeft -= dtMs;
    // Interior / always-night → leave the preset's own mood + grade untouched.
    if (this.exempt) return;
    // Reduce-motion → hold (the modulation ticker is off there anyway, so this
    // just avoids emitting churn that would do nothing).
    if (getMotionPrefs().reduceMotion) return;
    const st = this.clock.state();
    const mood = this.baseMood * st.mood; // base × diurnal (combat multiplies later)
    const force = this.settleMsLeft > 0; // re-assert every frame during the settle window
    const bus = EventBus.getInstance();
    if (force || Math.abs(mood - this.lastMoodEmitted) > 0.01) {
      this.lastMoodEmitted = mood;
      bus.emit(EVENTS.SCENE_MOOD, { intensity: mood });
    }
    // Phase 2 — push the eased time grade + named phase so AtmosphereLayer can
    // blend the authored colorGrade + apply per-layer day-response. Throttled
    // on the grade value (the colors ease slowly; the strength is the visible
    // knob). The settle window re-asserts every frame so a fresh mount picks it
    // up even if the eased delta is momentarily below threshold.
    const g = st.grade;
    const prev = this.lastGradeEmitted;
    const gradeChanged = !prev
      || prev.top !== g.top || prev.bottom !== g.bottom
      || Math.abs(prev.strength - g.strength) > 0.005;
    if (force || gradeChanged) {
      this.lastGradeEmitted = { top: g.top, bottom: g.bottom, strength: g.strength };
      bus.emit(EVENTS.TIME_OF_DAY, { phase: st.phase, phase01: st.phase01, grade: g });
    }
  }

  /** The loading level's authored base scene-mood (via MapSystem, set before
   *  LEVEL_LOADED fires). Falls back to 1 if the level isn't current yet. */
  private readBaseMood(levelId: string): number {
    try {
      const lvl = ServiceRegistry.getInstance().get('MapSystem', { optional: true })?.getCurrentLevel?.();
      if (lvl && (lvl as { id?: string }).id === levelId) return baseSceneMoodOf(lvl);
    } catch { /* MapSystem not ready */ }
    return 1;
  }

  /** Debug/QA — jump the clock + re-assert on the next tick (the 🌗 tuner / console).
   *  Opens a full SETTLE_MS window so both SCENE_MOOD + TIME_OF_DAY are force-emitted
   *  every frame long enough to survive a camera-settle re-mount (which resets
   *  the atmosphere's mood + grade to the authored base). */
  setPhase01(p: number): void {
    this.clock.setPhase01(p);
    this.lastMoodEmitted = -1;
    this.lastGradeEmitted = null;
    this.settleMsLeft = SETTLE_MS;
  }

  /** Current clock phase, 0..1 (tuner readout). */
  get phase01(): number {
    return this.clock.phase01;
  }

  /** Current interpolated clock state (tuner readout). */
  state(): TimeOfDayState {
    return this.clock.state();
  }

  stop(): void {
    this.unsubTick?.();
    this.unsubTick = null;
    this.unsubLevel?.();
    this.unsubLevel = null;
    this.started = false;
  }
}

/** Process singleton — one day/night clock for the client. */
export const timeOfDayDriver = new TimeOfDayDriver();

/** Start the day/night clock bridge (called once at game bootstrap). Idempotent. */
export function startTimeOfDayDriver(): void {
  timeOfDayDriver.start();
}
